/**
 * How a visitor is reaching the internet, inferred from browser and network shape.
 *
 * People in heavily restricted or low-infrastructure regions rarely arrive on a
 * plain consumer ISP connection. The common patterns are:
 *
 *   • Tor Browser — spoofs the time zone to UTC, reports en-US regardless of
 *     locale, exposes no Network Information API, and uses letterboxed window
 *     sizes. That combination is highly distinctive.
 *   • Satellite (Starlink and similar) — common where terrestrial infrastructure
 *     is degraded or filtered. Identified by the operator's ASN.
 *   • Residential proxy networks — traffic exits through a real consumer line
 *     rented from a proxy vendor, so geo-IP looks legitimate. Only the ASN gives
 *     it away, and only for known vendors.
 *   • Cloud desktop / VDI — a hosted Windows or Linux desktop in another country,
 *     driven remotely. Looks like a datacenter IP with a desktop browser.
 *   • Circumvention proxies (Psiphon, Lantern, Outline) — usually exit through
 *     cloud hosting, so they present as hosting rather than as a consumer VPN.
 *
 * Every one of these is a HEURISTIC. They describe a population, are wrong for
 * individuals, and must never be treated as proof about a person.
 */
import type { NetworkClass } from './vpnProviders'

export type AccessMethod =
  | 'tor_browser'
  | 'satellite'
  | 'residential_proxy'
  | 'cloud_desktop'
  | 'commercial_vpn'
  | 'hosting_or_proxy'
  | 'corporate_egress'
  | 'consumer_isp'
  | 'unknown'

export const ACCESS_METHOD_LABELS: Record<AccessMethod, string> = {
  tor_browser: 'Tor Browser',
  satellite: 'Satellite (Starlink et al.)',
  residential_proxy: 'Residential proxy network',
  cloud_desktop: 'Cloud desktop / VDI',
  commercial_vpn: 'Commercial VPN',
  hosting_or_proxy: 'Hosting / circumvention proxy',
  corporate_egress: 'Corporate network',
  consumer_isp: 'Consumer ISP',
  unknown: 'Unidentified'
}

interface AccessSignals {
  networkClass: NetworkClass | null
  timeZone: string | null
  language: string | null
  hasNetworkInformation: boolean
  isMobileDevice: boolean
}

/**
 * Tor Browser's anti-fingerprinting is itself a fingerprint: UTC time zone, en-US
 * locale, and no Network Information API, all at once. Requiring all three keeps
 * false positives low — plenty of people are genuinely in UTC.
 */
function looksLikeTorBrowser(signals: AccessSignals): boolean {
  const isUtc = signals.timeZone === 'UTC' || signals.timeZone === 'Etc/UTC'
  const isEnUs = signals.language === 'en-US'
  return isUtc && isEnUs && !signals.hasNetworkInformation && !signals.isMobileDevice
}

export function classifyAccessMethod(signals: AccessSignals): AccessMethod {
  if (looksLikeTorBrowser(signals)) return 'tor_browser'

  switch (signals.networkClass) {
    case 'satellite':
      return 'satellite'
    case 'residential_proxy':
      return 'residential_proxy'
    case 'vpn':
      return 'commercial_vpn'
    case 'business':
      return 'corporate_egress'
    case 'hosting':
      // A desktop browser on a datacenter IP is the shape of a remote desktop;
      // a mobile one is more likely a proxy app.
      return signals.isMobileDevice ? 'hosting_or_proxy' : 'cloud_desktop'
    case 'isp':
      return 'consumer_isp'
    default:
      return 'unknown'
  }
}

/** Collects the signals from the current browser. */
export function readAccessSignals(
  networkClass: NetworkClass | null,
  timeZone: string | null
): AccessSignals {
  return {
    networkClass,
    timeZone,
    language: navigator.language || null,
    hasNetworkInformation: 'connection' in navigator,
    isMobileDevice: /Android|iPhone|iPad|iPod/i.test(navigator.userAgent)
  }
}
